import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { KeyRound, Mail } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useSEO({
    title: 'Recuperar contraseña | AutoMarket Colombia',
    description: 'Solicita un enlace para restablecer tu contraseña.',
    canonical: '/olvide-contrasena',
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-md px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
          <KeyRound className="h-7 w-7 text-slate-600" /> Recuperar contraseña
        </h1>
        <p className="mt-3 text-slate-600 text-sm">
          Ingresa el correo asociado a tu cuenta. Te enviaremos las instrucciones para restablecerla.
        </p>

        {!result && (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Correo</label>
              <Input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@ejemplo.com"
                className="rounded-none mt-1"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-btn"
              className="w-full rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold"
            >
              {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
            </Button>
            <Link
              to="/login"
              data-testid="forgot-back-login"
              className="block text-center text-sm font-semibold text-blue-700 hover:underline"
            >
              ← Volver al inicio de sesión
            </Link>
          </form>
        )}

        {result && (
          <div className="mt-8 border border-slate-200 bg-white p-5 space-y-4" data-testid="forgot-success">
            <div className="flex items-start gap-2">
              <Mail className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <div className="font-display font-bold text-slate-900">Solicitud recibida</div>
                <p className="text-sm text-slate-600 mt-1">{result.message}</p>
              </div>
            </div>

            <Link to="/login" data-testid="forgot-success-back-login" className="block text-center text-sm font-semibold text-blue-700 hover:underline">
              ← Volver al inicio de sesión
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
